/**
 * Organization-scoped shared resources — Zod schemas + response types.
 *
 * Org resources are linked into hubs as read-only shadow rows. The link surface
 * lives at `/api/setup/hubs/:hub_id/org-resources/*`; the canonical CRUD lives
 * at `/api/setup/organization-resources/*`. Skill resources are out of scope
 * for v1 — only `resource_type = 'knowledge'` is accepted.
 */

import { z } from 'zod';
import type { UploadSkillZipResponse } from './resources';

// ---------------------------------------------------------------------------
// Param schemas
// ---------------------------------------------------------------------------

export const orgResourceIdParam = z.object({
  id: z.string().uuid(),
});

export const orgResourceFileIdParam = z.object({
  file_id: z.string().uuid(),
});

export const orgResourceFolderIdParam = z.object({
  folder_id: z.string().uuid(),
});

/**
 * `:id` is the hub id, `:org_resource_id` is the org resource being unlinked.
 * Used by `DELETE /api/setup/hubs/:id/org-resources/:org_resource_id`.
 */
export const hubOrgResourceParam = z.object({
  id: z.string().uuid(),
  org_resource_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Query schemas
// ---------------------------------------------------------------------------

export const listOrgResourcesQuery = z.object({
  organization_id: z.string().uuid(),
  hub_id: z.string().uuid().optional(),
});

export const orgIdQuery = z.object({
  organization_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Body schemas — canonical CRUD
// ---------------------------------------------------------------------------

export const createOrgResourceBody = z.object({
  organization_id: z.string().uuid(),
  resource_name: z.string().min(1).max(255),
  resource_description: z.string().max(1000).nullish(),
  resource_type: z.enum(['knowledge', 'skill']).default('knowledge'),
  // Skill-only fields. Required for `resource_type = 'skill'` (validated in
  // the route layer since cross-field refinement complicates the inferred
  // input type for callers). Ignored for `knowledge`.
  skill_name: z.string().min(1).max(255).nullish(),
  skill_version: z.string().max(50).nullish(),
  folder_metadata_schema: z.record(z.unknown()).optional(),
  file_metadata_schema: z.record(z.unknown()).optional(),
  enabled: z.boolean().optional(),
  tags: z.array(z.string().uuid()).optional(),
  environment: z.enum(['preview', 'production', 'all']).optional(),
});

export const updateOrgResourceBody = z.object({
  resource_name: z.string().min(1).max(255).optional(),
  resource_description: z.string().max(1000).nullish(),
  skill_name: z.string().min(1).max(255).nullish(),
  skill_version: z.string().max(50).nullish(),
  folder_metadata_schema: z.record(z.unknown()).optional(),
  file_metadata_schema: z.record(z.unknown()).optional(),
  enabled: z.boolean().optional(),
  tags: z.array(z.string().uuid()).optional(),
  environment: z.enum(['preview', 'production', 'all']).optional(),
});

export const createOrgResourceFolderBody = z.object({
  org_resource_id: z.string().uuid(),
  folder_name: z.string().min(1).max(255),
  folder_description: z.string().max(1000).nullish(),
  parent_folder_id: z.string().uuid().nullish(),
  order_index: z.number().int().nonnegative().optional(),
  enabled: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const updateOrgResourceFolderBody = z.object({
  folder_name: z.string().min(1).max(255).optional(),
  folder_description: z.string().max(1000).nullish(),
  parent_folder_id: z.string().uuid().nullish(),
  order_index: z.number().int().nonnegative().optional(),
  enabled: z.boolean().optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const uploadOrgResourceFileBody = z.object({
  org_resource_id: z.string().uuid(),
  folder_id: z.string().uuid().nullish(),
  file_name: z.string().min(1).max(255),
  file_data: z.string(), // base64
  mime_type: z.string().optional(),
  file_size: z.number().int().nonnegative().optional(),
  resource_file_tags: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
});

export const updateOrgResourceFileBody = z.object({
  resource_file_title: z.string().min(1).max(255).optional(),
  folder_id: z.string().uuid().nullish(),
  order_index: z.number().int().nonnegative().optional(),
  enabled: z.boolean().optional(),
  resource_file_tags: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
});

/**
 * Bulk-materialize an org skill resource from a ZIP containing SKILL.md and
 * supporting files. Mirrors the hub-side `uploadSkillZipBody` shape, scoped
 * via the route `:id` param + `organization_id` query.
 */
export const uploadOrgSkillZipBody = z.object({
  zip_data: z.string().min(1),
  replace_existing: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Body schemas — hub linking
// ---------------------------------------------------------------------------

export const linkOrgResourceBody = z.object({
  org_resource_id: z.string().uuid(),
});

// ---------------------------------------------------------------------------
// Inferred input types
// ---------------------------------------------------------------------------

export type CreateOrgResourceInput = z.infer<typeof createOrgResourceBody>;
export type UpdateOrgResourceInput = z.infer<typeof updateOrgResourceBody>;
export type CreateOrgResourceFolderInput = z.infer<typeof createOrgResourceFolderBody>;
export type UpdateOrgResourceFolderInput = z.infer<typeof updateOrgResourceFolderBody>;
export type UploadOrgResourceFileInput = z.infer<typeof uploadOrgResourceFileBody>;
export type UpdateOrgResourceFileInput = z.infer<typeof updateOrgResourceFileBody>;
export type UploadOrgSkillZipInput = z.infer<typeof uploadOrgSkillZipBody>;
export type LinkOrgResourceInput = z.infer<typeof linkOrgResourceBody>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface OrgResourceResponse {
  org_resource_id: string;
  organization_id: string;
  resource_name: string;
  resource_description: string | null;
  resource_type: 'knowledge' | 'skill';
  /** Set only when resource_type='skill'. */
  skill_name: string | null;
  /** Set only when resource_type='skill'. Defaults to '1.0.0'. */
  skill_version: string | null;
  folder_metadata_schema: Record<string, unknown>;
  file_metadata_schema: Record<string, unknown>;
  enabled: boolean;
  tags: string[];
  environment: 'preview' | 'production' | 'all';
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface OrgResourceFileResponse {
  org_resource_file_id: string;
  org_resource_id: string;
  resource_file_title: string;
  resource_file_tags: string[];
  folder_id: string | null;
  file_id: string;
  file_path: string;
  file_name: string;
  mime_type: string | null;
  file_size: number | null;
  file_hash: string | null;
  order_index: number;
  enabled: boolean;
  metadata: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface OrgResourceFolderResponse {
  org_resource_folder_id: string;
  org_resource_id: string;
  folder_name: string;
  folder_description: string | null;
  parent_folder_id: string | null;
  order_index: number;
  enabled: boolean;
  metadata: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * One entry of `GET /api/setup/hubs/:hub_id/org-resources/`. Combines the org
 * resource shape with link state for this hub. `policy_match` lets the UI
 * surface a warning when a previously-linked resource no longer matches the
 * hub's tag/environment policy.
 */
export interface HubOrgResourceEntry extends OrgResourceResponse {
  linked: boolean;
  linked_at: string | null;
  policy_match: 'ok' | 'stale_tags' | 'stale_env';
}

export interface ListOrgResourcesResponse {
  data: OrgResourceResponse[];
}

export interface GetOrgResourceResponse {
  data: OrgResourceResponse;
}

export interface CreateOrgResourceResponse {
  data: OrgResourceResponse;
}

export interface UpdateOrgResourceResponse {
  data: OrgResourceResponse;
}

export interface DeleteOrgResourceResponse {
  data: { deleted: true; id: string };
}

export interface ListOrgResourceFilesResponse {
  data: OrgResourceFileResponse[];
}

export interface UploadOrgResourceFileResponse {
  data: OrgResourceFileResponse;
}

/**
 * Response from `POST /api/setup/organization-resources/:id/files/upload-zip`.
 * Structurally identical to the hub-side `UploadSkillZipResponse` — same fields,
 * same semantics; aliased so a change to either flows to both.
 */
export type UploadOrgSkillZipResponse = UploadSkillZipResponse;

export interface UpdateOrgResourceFileResponse {
  data: OrgResourceFileResponse;
}

export interface DeleteOrgResourceFileResponse {
  data: { deleted: true; id: string };
}

export interface ListOrgResourceFoldersResponse {
  data: OrgResourceFolderResponse[];
}

export interface CreateOrgResourceFolderResponse {
  data: OrgResourceFolderResponse;
}

export interface UpdateOrgResourceFolderResponse {
  data: OrgResourceFolderResponse;
}

export interface DeleteOrgResourceFolderResponse {
  data: { deleted: true; id: string };
}

export interface ListHubOrgResourcesResponse {
  data: HubOrgResourceEntry[];
}

export interface LinkOrgResourceResponse {
  data: { resource_id: string; linked: true };
}

export interface UnlinkOrgResourceResponse {
  data: { deleted: true; org_resource_id: string; hub_id: string };
}

/**
 * Returned by `GET /api/setup/organization-resources/files/:file_id/content`
 * for admin preview of an org-resource file. Provides a short-lived signed
 * URL the browser can fetch directly — avoids streaming bytes through the
 * worker and keeps the existing `/files/auth/...` org-membership check on
 * the signed-URL serving route.
 */
export interface OrgResourceFileContentResponse {
  data: {
    file_id: string;
    file_name: string;
    mime_type: string | null;
    file_size: number | null;
    signed_url: string;
    expires_in_seconds: number;
  };
}
